import { useTranslation } from 'react-i18next'
import { Resource } from '../../../../types'
import RadioInput from '../../../../ui/form/RadioInput'
import Iframe from '../../../../ui/Iframe'
import { useContext, useEffect, useState } from 'react'
import { ProjectContext } from '../../../../contexts'
import api from '../../../../api'
import type { ChannelConfig, CreateOptionsProps, CreateTemplateFn } from '../TemplateCreateModal'
import './LayoutPicker.css'
import { createDefaultPopupData } from '../../editor/puck/defaultPopup'
import { buildPopupHtml } from '../../editor/puck/buildPopupHtml'

const InAppCreateOptions = ({ form, campaign }: CreateOptionsProps) => {
    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)
    const [layouts, setLayouts] = useState<Resource[]>([])

    useEffect(() => {
        api.resources.all(project.id, 'layout')
            .then(setLayouts)
            .catch(() => {})
    }, [project.id])

    const hasLayouts = layouts.length > 0
    const hasExistingTemplates = campaign.templates.length > 0

    return (
        <>
            <RadioInput.Field
                form={form}
                name="data.editor"
                label={t('editor_type')}
                options={[
                    { key: 'code', label: t('code') },
                    { key: 'popup', label: t('popup_builder') },
                ]}
            />
            {hasLayouts && !hasExistingTemplates && form.watch('data.editor') !== 'popup' && (
                <LayoutPicker
                    layouts={layouts}
                    value={form.watch('data.layout_id') ?? ''}
                    onChange={(value) => {
                        form.setValue('data.layout_id', value)
                        const layout = layouts.find(l => String(l.id) === value)
                        form.setValue('data.layout_html' as any, layout?.value?.html ?? '')
                    }}
                />
            )}
        </>
    )
}

const inAppCreateTemplate: CreateTemplateFn = async ({ data, initialData }) => {
    if (data?.editor === 'popup') {
        const puckData = createDefaultPopupData()
        return {
            data: {
                ...initialData,
                editor: 'popup',
                html: buildPopupHtml(puckData),
                puck_data: puckData,
            },
        }
    }

    if (initialData?.layout_html) {
        initialData = { ...initialData, html: initialData.layout_html, type: 'html' }
    }

    delete initialData?.layout_id
    delete initialData?.layout_html

    return { data: initialData }
}

export const inAppChannel: ChannelConfig = {
    CreateOptions: InAppCreateOptions,
    createTemplate: inAppCreateTemplate,
}

interface LayoutPickerProps {
    layouts: Resource[]
    value: string
    onChange: (value: string) => void
}

function LayoutPicker({ layouts, value, onChange }: LayoutPickerProps) {
    const { t } = useTranslation()

    return (
        <div className="field">
            <label>{t('layout')}</label>
            <div className="layout-picker-grid">
                <div
                    className={`layout-picker-card${value === '' ? ' selected' : ''}`}
                    onClick={() => onChange('')}
                >
                    <div className="layout-picker-preview layout-picker-blank">
                        <span>{t('start_from_scratch')}</span>
                    </div>
                    <div className="layout-picker-label">{t('start_from_scratch')}</div>
                </div>
                {layouts.map(layout => (
                    <div
                        key={layout.id}
                        className={`layout-picker-card${value === String(layout.id) ? ' selected' : ''}`}
                        onClick={() => onChange(String(layout.id))}
                    >
                        <div className="layout-picker-preview">
                            <Iframe
                                content={layout.prerender ?? layout.value?.html ?? ''}
                                allowScroll={false}
                            />
                        </div>
                        <div className="layout-picker-label">{layout.name}</div>
                    </div>
                ))}
            </div>
        </div>
    )
}
